import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Printer, FileDown } from 'lucide-react';
import { type TelemetryPassportClientResponse } from '@siliconx/shared';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { api, throwIfNotOk } from '../../lib/api';
import type { DownloadUrlResponse } from '../../lib/types';
import { AssetPassportBody } from './AssetPassport';
import { LedgerPassport } from './LedgerPassport';
import { qk } from '../../lib/queryKeys';
import { formatDate } from '../../lib/format';
import { Card } from '@siliconx/shared/ui/Card';

/**
 * The single digital-passport surface, keyed by the public Fingerprint
 * (`<prefix>-<token>`), never asset.id: the page lives at
 * /passport/$fingerprint (routes/passport.$fingerprint.tsx) and every API call
 * below goes to /api/client/passport/:fingerprint. The API resolves the
 * Fingerprint under the caller's own RLS session; the Fingerprint grants
 * nothing, so an unauthorized viewer just sees the "not available" state.
 * An owner reaches it from the asset / registry, a buyer from a listing
 * (`?listing=` supplies the back-link).
 */

interface ApiError extends Error {
  status?: number;
  /** Parsed JSON error body attached by throwIfNotOk. */
  body?: unknown;
}

/**
 * GET /api/client/passport/:fingerprint = the passport plus a presence-only
 * serial flag (SIL-1991). `true`/`false` for the owning org, `null`/absent when
 * unknown (non-owner viewer). It is a boolean - never the serial itself.
 */
export type PassportClientPayload = TelemetryPassportClientResponse & {
  hasRegisteredSerial?: boolean | null;
};

export const SERIAL_REQUIRED_HELPER = 'Add a serial number to generate a passport.';
const GENERIC_GENERATE_ERROR = 'Could not generate the PDF right now — please try again.';

/**
 * SIL-1965: the API refuses generation for an asset with no registered serial
 * (422 { error: { code: 'serial_required', message } }). Returns that message
 * so a stale page / race shows the real reason, not the generic error.
 */
function serialRequiredMessage(err: ApiError): string | null {
  if (err.status !== 422) return null;
  const error = (err.body as { error?: { code?: unknown; message?: unknown } } | undefined)?.error;
  if (error?.code !== 'serial_required') return null;
  return typeof error.message === 'string' && error.message.length > 0 ? error.message : SERIAL_REQUIRED_HELPER;
}

interface GenerateArtifactResponse {
  artifactId: string;
  sha256Hex: string;
  generatedAt: string;
}

/**
 * RUF-556 — on-demand "Generate PDF Artifact" trigger. POSTs the render
 * request (synchronous — the button shows "Generating…" for the render
 * Lambda's ~20s budget), then immediately fetches a presigned download URL
 * for the freshly-minted artifact and opens it. RUF-557: also refreshes the
 * "Issued artifacts" list on success so the new row appears without a
 * manual page reload.
 *
 * Only rendered for the owning org: migration 607's INSERT WITH CHECK admits
 * the owner, or a buyer holding a linking transaction — a browsing buyer's
 * click would 403 from the DB layer, so the button is not offered to them.
 */
export function GeneratePdfButton({
  fingerprint,
  serialMissing = false,
}: {
  fingerprint: string;
  /** SIL-1991: the asset has no registered serial - generation would 422. */
  serialMissing?: boolean;
}) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation<DownloadUrlResponse, ApiError, void>({
    mutationFn: async () => {
      const genRes = await api.api.client.passport[':fingerprint'].generate.$post({
        param: { fingerprint },
      });
      await throwIfNotOk(genRes);
      const generated = (await genRes.json()) as unknown as GenerateArtifactResponse;
      const dlRes = await api.api.client.passport[':fingerprint'].artifacts[
        ':artifactId'
      ].download.$get({ param: { fingerprint, artifactId: generated.artifactId } });
      await throwIfNotOk(dlRes);
      return (await dlRes.json()) as unknown as DownloadUrlResponse;
    },
    onMutate: () => setErrorMessage(null),
    onSuccess: (data) => {
      window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
      void queryClient.invalidateQueries({ queryKey: qk.assets.passportArtifacts(fingerprint) });
    },
    onError: (err) => {
      setErrorMessage(serialRequiredMessage(err) ?? GENERIC_GENERATE_ERROR);
    },
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending || serialMissing}
        aria-describedby={serialMissing ? 'passport-serial-required' : undefined}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FileDown className="size-4" /> {mutation.isPending ? 'Generating…' : 'Generate PDF Artifact'}
      </button>
      {serialMissing ? (
        <p id="passport-serial-required" className="text-xs text-muted-foreground">
          {SERIAL_REQUIRED_HELPER}
        </p>
      ) : null}
      {errorMessage ? <p className="text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
}

export function usePassportQuery(fingerprint: string) {
  return useQuery<PassportClientPayload, ApiError>({
    queryKey: qk.assets.passport(fingerprint),
    queryFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].$get({ param: { fingerprint }, query: {} });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
      }
      return (await res.json()) as unknown as PassportClientPayload;
    },
    retry: (failCount, err) => err.status !== 404 && failCount < 2,
  });
}

/**
 * Filename-safe asset label for the print/save-as-PDF title. Slashes and colons
 * would be rewritten by the OS save dialog, so strip them here.
 */
export function printLabel(passport: TelemetryPassportClientResponse | undefined): string {
  const parts = passport
    ? [
        passport.identity.manufacturerName,
        passport.identity.skuModelName,
        passport.identity.serialNumber,
      ].filter(Boolean)
    : [];
  const label = parts.length > 0 ? parts.join(' ') : 'Asset';
  return label.replace(/[\\/:*?"<>|]/g, '-');
}

export function PassportSurface({
  fingerprint,
  backLink,
  canGenerate = false,
}: {
  fingerprint: string;
  backLink: ReactNode;
  /** Owner-only — see GeneratePdfButton's note on migration 607. */
  canGenerate?: boolean;
}) {
  const passportQ = usePassportQuery(fingerprint);

  return (
    <div className="passport-print mx-auto max-w-3xl space-y-6">
      {/* Breadcrumb + print action — hidden on the printed/PDF page. This is
       * a print surface (RUF-555, spec §1.5 allowlist) — it deliberately
       * drops PageHeader/nav, not just here but by policy; the heading below
       * is a styled <div>, not an <h1>, so it never trips the bare-<h1> check
       * even though this file is already on that check's allowlist. */}
      <div className="flex items-center justify-between gap-3 print:hidden">
        <div className="flex items-center gap-3">
          {backLink}
          <span className="text-muted-foreground">/</span>
          <span className="text-sm font-medium text-foreground">Digital passport</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              // The browser derives the saved-PDF filename from document.title,
              // which AppShell sets to a static "SiliconX" - every asset's
              // passport would download as the same "SiliconX.pdf". Name the
              // asset (never the user or org - SIL-1529) for the duration of
              // the print, then restore.
              const previousTitle = document.title;
              document.title = `SiliconX Passport - ${printLabel(passportQ.data)}`;
              window.addEventListener(
                'afterprint',
                () => {
                  document.title = previousTitle;
                },
                { once: true },
              );
              document
                .querySelectorAll<HTMLDetailsElement>('.passport-print details')
                .forEach((el) => {
                  el.open = true;
                });
              requestAnimationFrame(() => {
                requestAnimationFrame(() => window.print());
              });
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
          >
            <Printer className="size-4" /> Print / Save as PDF
          </button>
          {/* RUF-556 — the immutable, hashed, verifiable artifact (distinct
              from the browser print-to-PDF above, which is NOT a stamped
              point-in-time record). */}
          {canGenerate ? (
            <GeneratePdfButton
              fingerprint={fingerprint}
              serialMissing={passportQ.data?.hasRegisteredSerial === false}
            />
          ) : null}
        </div>
      </div>

      <QueryBoundary
        query={passportQ}
        errorTitle="Couldn't load digital passport."
        emptyState={
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-muted-foreground">Digital passport not available for this asset.</p>
            </div>
          </div>
        }
      >
        {(passport) => {
          const title =
            passport.identity.manufacturerName && passport.identity.skuModelName
              ? `${passport.identity.manufacturerName} ${passport.identity.skuModelName}`
              : (passport.identity.serialNumber ?? 'Asset');

          return (
            <>
              <div>
                <div className="text-lg font-semibold text-foreground">Digital passport</div>
                <p className="text-sm text-muted-foreground">{title}</p>
              </div>

              {/* Shared §1-§5 stack + issued-artifacts card. */}
              <AssetPassportBody passport={passport} fingerprint={fingerprint} showArtifacts />

              {/* SIL-2373: REC status, results, history and evidence from the ledger. */}
              <LedgerPassport fingerprint={fingerprint} />
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Listing-detail summary strip — replaces the full §1-§5 embed that used to
// sit below the offer form on a single-asset listing. Same four facts a lot
// member row already shows (health, tier, coverage), so a buyer reads one
// consistent shape whether the listing is a lot or a single asset, and the
// offer action stays above the fold.
// ---------------------------------------------------------------------------

interface IssuedArtifactRow {
  artifactId: string;
  generatedAt: string;
}

/** Latest artifact the caller is allowed to see (RLS-scoped, migration 777). */
function useLatestArtifact(fingerprint: string) {
  return useQuery<IssuedArtifactRow | null, ApiError>({
    queryKey: [...qk.assets.passportArtifacts(fingerprint), 'latest'],
    queryFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].artifacts.$get({
        param: { fingerprint },
      });
      await throwIfNotOk(res);
      const body = (await res.json()) as unknown as { data: IssuedArtifactRow[] };
      return body.data[0] ?? null;
    },
    retry: false,
  });
}

/**
 * The stamped-PDF download, as behaviour without presentation: resolve the
 * latest artifact this caller may see, presign it, open it.
 *
 * Exported because SIL-1349's node Passport drawer needs the SAME existing
 * report route behind its own footer button ("Download SX Report", per the
 * handoff's own wording for the outline action) and a second copy of this
 * two-step fetch-then-presign dance is a second place to get the RLS-scoped
 * "no artifact yet" case wrong. The hook owns the requests; each caller owns
 * its own label and chrome.
 *
 * `artifact` is null both while loading and when the caller may see none — a
 * button over this hook renders nothing in either case rather than offering a
 * download that would 404.
 */
export function usePassportArtifactDownload(fingerprint: string) {
  const { data: artifact } = useLatestArtifact(fingerprint);
  const mutation = useMutation<DownloadUrlResponse, ApiError, void>({
    mutationFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].artifacts[
        ':artifactId'
      ].download.$get({ param: { fingerprint, artifactId: artifact!.artifactId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DownloadUrlResponse;
    },
    onSuccess: (data) => window.open(data.downloadUrl, '_blank', 'noopener,noreferrer'),
  });

  return {
    artifact: artifact ?? null,
    download: () => mutation.mutate(),
    isPending: mutation.isPending,
  };
}

function ArtifactDownloadLink({ fingerprint }: { fingerprint: string }) {
  const { artifact, download, isPending } = usePassportArtifactDownload(fingerprint);

  if (!artifact) return null;

  return (
    <button
      type="button"
      onClick={download}
      disabled={isPending}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:underline disabled:opacity-60"
    >
      <FileDown className="size-4" />
      {isPending ? 'Opening…' : 'Download PDF passport'}
      {/* The artifact is a point-in-time attestation minted when the listing
          was published — it does NOT track telemetry accrued since, so the
          date is part of the label, not a detail. */}
      <span className="font-normal text-muted-foreground">
        (as published {formatDate(artifact.generatedAt)})
      </span>
    </button>
  );
}

export function PassportSummaryStrip({
  fingerprint,
  listingId,
}: {
  fingerprint: string;
  listingId: string;
}) {
  const { data: passport, isLoading } = usePassportQuery(fingerprint);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading passport…</p>;
  }

  if (!passport) {
    return (
      <Card className="text-sm text-muted-foreground">
        Digital passport not available for this asset.
      </Card>
    );
  }

  const { health, coverage } = passport;
  const coverageWindow =
    coverage.firstSeen && coverage.lastSeen
      ? `${formatDate(coverage.firstSeen)} – ${formatDate(coverage.lastSeen)}`
      : `${coverage.observedDays} days observed`;

  return (
    <Card className="space-y-4">
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Health
          </dt>
          <dd className="mt-1 font-num text-lg font-semibold text-foreground">
            {health ? Math.round(health.overallScore) : '—'}
            {health && <span className="text-xs font-normal text-muted-foreground">/100</span>}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Confidence
          </dt>
          <dd className="mt-1 text-sm text-foreground">{coverage.confidenceTier ?? '—'}</dd>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Telemetry coverage
          </dt>
          <dd className="mt-1 text-sm text-foreground">{coverageWindow}</dd>
        </div>
      </dl>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <ArtifactDownloadLink fingerprint={fingerprint} />
        <Link
          to="/passport/$fingerprint"
          params={{ fingerprint }}
          search={{ listing: listingId }}
          className="text-sm font-medium text-foreground hover:underline"
        >
          View full passport &rarr;
        </Link>
      </div>
    </Card>
  );
}
